import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { SMALL_IMAGE_UPSCALE_DEFAULT_PERCENT } from '@xphoto/shared';
import { fetchAppConfig } from './app-config-api';

/**
 * Contexte de CONFIGURATION GLOBALE (Incrément B — coupe-circuit IA ; LOT 3 — nom du
 * site).
 *
 * Expose à toute la zone authentifiée :
 *  - `aiEnabled` : les composants qui rendent une entrée IA (« Tagger avec l'IA ») le
 *    lisent pour la masquer quand l'IA est désactivée. La bascule superadmin met à jour
 *    le contexte via `setAiEnabled` → l'entrée disparaît immédiatement, sans rechargement.
 *  - `appName`   : nom du site éditable par le superadmin (console « Général »). La
 *    section met à jour le contexte via `setAppName` → l'UI suit sans rechargement.
 *
 * DÉFAUTS PRUDENTS tant que la valeur n'est pas chargée : `aiEnabled = true` (évite un
 * masquage brutal) et `appName = 'xphoto'` (nom historique). Le contexte a une valeur
 * par DÉFAUT non nulle → `useAppConfig` ne lève jamais hors provider (composants montés
 * en test sans le provider → comportement actuel).
 */

export interface AppConfigContextValue {
  /** Fonctions LLM/IA activées ? (défaut prudent `true`). */
  aiEnabled: boolean;
  /** Met à jour l'état local du drapeau (après une bascule superadmin). */
  setAiEnabled: (enabled: boolean) => void;
  /** Nom du site (défaut prudent `'xphoto'`). */
  appName: string;
  /** Met à jour l'état local du nom (après un renommage superadmin). */
  setAppName: (name: string) => void;
  /**
   * Agrandissement maximal (%) des petites photos dans la visionneuse (défaut prudent
   * `SMALL_IMAGE_UPSCALE_DEFAULT_PERCENT`). Lu par `ZoomableImage` pour plafonner le
   * zoom au repos.
   */
  smallImageUpscalePercent: number;
  /** Met à jour l'état local de l'agrandissement (après un réglage superadmin). */
  setSmallImageUpscalePercent: (percent: number) => void;
  /**
   * JETON DE CONCURRENCE des réglages globaux (audit A6) : l'`updatedAt` de la dernière
   * lecture. Le formulaire superadmin le renvoie en `expectedUpdatedAt` → une écriture
   * concurrente est refusée (409) au lieu d'écraser silencieusement. `undefined` tant que
   * la config n'est pas chargée (ou hors provider) ⇒ ancien comportement, pas de blocage.
   */
  updatedAt: string | undefined;
  /** Met à jour le jeton après un PATCH réussi (le serveur renvoie l'état résultant). */
  setUpdatedAt: (updatedAt: string | undefined) => void;
}

/** Nom de site par défaut (repli avant chargement / hors provider). */
export const DEFAULT_APP_NAME = 'xphoto';

const DEFAULT_VALUE: AppConfigContextValue = {
  aiEnabled: true,
  setAiEnabled: () => {
    /* no-op hors provider (défaut sûr) */
  },
  appName: DEFAULT_APP_NAME,
  setAppName: () => {
    /* no-op hors provider (défaut sûr) */
  },
  smallImageUpscalePercent: SMALL_IMAGE_UPSCALE_DEFAULT_PERCENT,
  setSmallImageUpscalePercent: () => {
    /* no-op hors provider (défaut sûr) */
  },
  updatedAt: undefined,
  setUpdatedAt: () => {
    /* no-op hors provider (défaut sûr) */
  },
};

const AppConfigContext = createContext<AppConfigContextValue>(DEFAULT_VALUE);

export function AppConfigProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [aiEnabled, setAiEnabled] = useState(true);
  const [appName, setAppName] = useState(DEFAULT_APP_NAME);
  const [smallImageUpscalePercent, setSmallImageUpscalePercent] = useState(
    SMALL_IMAGE_UPSCALE_DEFAULT_PERCENT,
  );
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);

  useEffect(() => {
    const controller = new AbortController();
    fetchAppConfig(controller.signal)
      .then((config) => {
        setAiEnabled(config.aiEnabled);
        setAppName(config.appName);
        setSmallImageUpscalePercent(config.smallImageUpscalePercent);
        setUpdatedAt(config.updatedAt);
      })
      .catch(() => {
        /* config indisponible : défauts prudents (IA visible, nom historique, plafond 150 %) conservés. */
      });
    return () => controller.abort();
  }, []);

  // Valeur de contexte MÉMOÏSÉE (audit B5) — comme `AuthProvider`, `I18nProvider` et
  // `ThemeProvider`. Un objet littéral neuf à chaque rendu forçait le re-rendu de TOUS les
  // consommateurs (dont `ZoomableImage`) à chaque rendu du provider — or il est monté dans
  // `AppChrome`, qui re-rend à chaque navigation via `useLocation`. Les setters sont des
  // fonctions `useState`, stables : les dépendances se limitent aux valeurs.
  const value = useMemo<AppConfigContextValue>(
    () => ({
      aiEnabled,
      setAiEnabled,
      appName,
      setAppName,
      smallImageUpscalePercent,
      setSmallImageUpscalePercent,
      updatedAt,
      setUpdatedAt,
    }),
    [aiEnabled, appName, smallImageUpscalePercent, updatedAt],
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

/** Accès au contexte de configuration globale (défaut sûr hors provider). */
export function useAppConfig(): AppConfigContextValue {
  return useContext(AppConfigContext);
}
